/**
 * useTemplateVariables
 *
 * Provides the list of available template variables that users can insert into
 * rich-text content (before-trip, after-trip, included, excluded, optional, etc.).
 * These variables are replaced with computed values when the travel book / preview
 * is rendered (both on the backend in PublicController::getPreviewData and on the
 * frontend as a fallback).
 *
 * Supported formats:
 *   {{NO-OF-DAYS}}         (primary — double curly braces, uppercase, hyphens)
 *   [no_of_days]           (legacy — square brackets, lowercase, underscores)
 */

export interface TemplateVariable {
  /** The token inserted into content, e.g. {{NO-OF-DAYS}} */
  token: string;
  /** Short label shown in the insertion dropdown */
  label: string;
  /** Longer description of what the variable resolves to */
  description: string;
}

export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  {
    token: "{{NO-OF-DAYS}}",
    label: "No. of Days",
    description: "Total number of days in the trip",
  },
  {
    token: "{{NO-OF-NIGHTS}}",
    label: "No. of Nights",
    description: "Total number of accommodation nights in the trip",
  },
  {
    token: "{{SUPPLIER_TYPE_HOTEL}}",
    label: "Hotel Type",
    description: "The word 'hotel' (or translated type) for use in a sentence",
  },
  {
    token: "{{SUPPLIER_TYPE_REFUGE}}",
    label: "Refuge Type",
    description: "The word 'refuge' (or translated type) for use in a sentence",
  },
  {
    token: "{{HOTEL_COUNT}}",
    label: "Hotel Count",
    description: "Number of hotel accommodations in the trip",
  },
  {
    token: "{{REFUGE_COUNT}}",
    label: "Refuge Count",
    description: "Number of refuge accommodations in the trip",
  },
];

export interface TemplateVariableValues {
  noOfDays: number;
  noOfNights: number;
  hotelCount: number;
  refugeCount: number;
  hotelType: string;
  refugeType: string;
}

/**
 * Replace template variables in a string with computed values.
 * Used as a client-side fallback when the backend hasn't already replaced them.
 */
export function replaceTemplateVariables(
  content: string | undefined | null,
  values: TemplateVariableValues
): string {
  if (!content) return "";

  const search = [
    "{{NO-OF-DAYS}}",
    "{{NO-OF-NIGHTS}}",
    "{{SUPPLIER_TYPE_HOTEL}}",
    "{{SUPPLIER_TYPE_REFUGE}}",
    "{{HOTEL_COUNT}}",
    "{{REFUGE_COUNT}}",
    "[no_of_days]",
    "[no_of_nights]",
  ];
  const replace = [
    String(values.noOfDays),
    String(values.noOfNights),
    String(values.hotelType),
    String(values.refugeType),
    String(values.hotelCount),
    String(values.refugeCount),
    String(values.noOfDays),
    String(values.noOfNights),
  ];

  return search.reduce(
    (text, token, index) => text.split(token).join(replace[index]),
    content
  );
}

export function useTemplateVariables() {
  return {
    variables: TEMPLATE_VARIABLES,
    replaceTemplateVariables,
  };
}
